<script setup lang="ts">
import type { RegisterContentItem } from '@su4r-frontend/api';
import type { FormFieldsWithError } from '~/types/client/clientFormModel';
import type { ClientRegisterModel } from '~/types/client/clientRegisterModel';

const props = defineProps<{
    data: RegisterContentItem;
}>();

const { alertSettings, hideAlert, showAlert } = useAlertToast();

const localeRoute = useLocaleRoute();
const { t } = useI18n();
const { flow } = props.data;

const body = reactive<FormFieldsWithError<ClientRegisterModel>>({
    occupation: {
        value: '',
        errorMessage: '',
    },
    firstName: {
        value: '',
        errorMessage: '',
    },
    lastName: {
        value: '',
        errorMessage: '',
    },
    insertion: {
        value: '',
        errorMessage: '',
    },
    email: {
        value: '',
        errorMessage: '',
    },
    password: {
        value: '',
        errorMessage: '',
    },
    repeatPassword: {
        value: '',
        errorMessage: '',
    },
});

const clearErrorMessages = (): void => {
    body.occupation.errorMessage = '';
    body.firstName.errorMessage = '';
    body.lastName.errorMessage = '';
    body.insertion.errorMessage = '';
    body.email.errorMessage = '';
    body.password.errorMessage = '';
    body.repeatPassword.errorMessage = '';
};

const clearFormData = (): void => {
    body.occupation.value = '';
    body.firstName.value = '';
    body.lastName.value = '';
    body.insertion.value = '';
    body.email.value = '';
    body.password.value = '';
    body.repeatPassword.value = '';
};

const isFormValid = (e: Event): boolean => {
    if (!e.target) return false;
    const formElement = e.target as HTMLFormElement;

    Array.from(formElement).forEach((el) => {
        const element = el as HTMLInputElement | HTMLSelectElement;

        if (element.name === 'occupation') {
            if (!element.validity.valid) {
                body.occupation.errorMessage = t('form.formErrorRequired');
            }
        }

        if (element.name === 'firstName') {
            if (element.validity.valueMissing) {
                body.firstName.errorMessage = t('form.formErrorRequired');
            } else if (element.validity.typeMismatch) {
                body.firstName.errorMessage = t('form.formErrorInvalidFormat');
            }
        }

        if (element.name === 'lastName') {
            if (element.validity.valueMissing) {
                body.lastName.errorMessage = t('form.formErrorRequired');
            } else if (element.validity.typeMismatch) {
                body.lastName.errorMessage = t('form.formErrorInvalidFormat');
            }
        }

        if (element.name === 'email') {
            if (element.validity.valueMissing) {
                body.email.errorMessage = t('form.formErrorRequired');
            } else if (element.validity.typeMismatch) {
                body.email.errorMessage = t('form.formErrorInvalidFormat');
            }
        }

        if (element.name === 'password') {
            if (element.validity.valueMissing) {
                body.password.errorMessage = t('form.formErrorRequired');
            } else if (element.validity.typeMismatch) {
                body.password.errorMessage = t('form.formErrorInvalidFormat');
            } else if (body.password.value.length < VALIDATION_LIMITS.MIN_PASSWORD_LENGTH) {
                body.password.errorMessage = t('form.formErrorPasswordLength');
            }
        }

        if (element.name === 'repeat-password') {
            if (element.validity.valueMissing) {
                body.repeatPassword.errorMessage = t('form.formErrorRequired');
            } else if (body.repeatPassword.value !== body.password.value) {
                body.repeatPassword.errorMessage = t('order.repeatPasswordError');
            }
        }
    });

    return Object.values(body)
        .filter((field) => 'errorMessage' in field)
        .every((field) => field.errorMessage === '');
};

const occupationOptions: RadioOption[] = [
    {
        value: 'practitioner',
        label: t('register.occupationPractitioner'),
        id: 'practitioner',
    },
    {
        value: 'developer',
        label: t('register.occupationDeveloper'),
        id: 'developer',
    },
];

const handleSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    clearErrorMessages();

    if (!isFormValid(e)) return;

    const formData = {
        occupation: body.occupation.value,
        firstName: body.firstName.value,
        lastName: body.lastName.value,
        insertion: body.insertion.value,
        email: body.email.value,
        password: body.password.value,
        repeatPassword: body.repeatPassword.value,
    };

    const res = await $fetch(`/api/account/register`, {
        body: JSON.stringify(formData),
        method: 'POST',
    });

    if (res) {
        clearFormData();
        showAlert(t('register.registerSuccess'), 'success');
    } else {
        showAlert(t('register.registerError'), 'error');
    }
};
</script>

<template>
    <section class="container grid grid-cols-12">
        <div class="col-span-12 pt-2 lg:col-span-8 lg:col-start-3">
            <form class="tile max-w-full bg-white" novalidate @submit.prevent="handleSubmit">
                <div class="grid gap-4 lg:gap-6">
                    <div>
                        <h4 class="text-xl">{{ flow.header }}</h4>
                        <div class="pink" v-html="flow.explanation" />
                    </div>
                    <hr class="text-black/10" />
                    <div class="grid gap-4">
                        <div class="grid gap-2">
                            <h6 class="font-body w-full font-semibold">
                                {{ $t('form.personalDetails') }}
                            </h6>

                            <Radio
                                v-model="body.occupation.value"
                                :error-message="body.occupation?.errorMessage"
                                name="occupation"
                                :validation-type="
                                    body.occupation?.errorMessage ? 'error' : undefined
                                "
                                :options="occupationOptions"
                                :required="true"
                                :label="$t('form.occupationQuestion')" />

                            <HelperText
                                v-if="flow.occupationDisclaimer"
                                class="w-full"
                                variant="info"
                                :text="flow.occupationDisclaimer" />
                        </div>
                        <div class="grid gap-4">
                            <Text
                                v-model="body.firstName.value"
                                :error-message="body.firstName?.errorMessage"
                                :validation-type="
                                    body.firstName?.errorMessage ? 'error' : undefined
                                "
                                name="firstName"
                                type="text"
                                :required="true"
                                :label="$t('form.firstNameLabel')" />
                            <Text
                                v-model="body.insertion.value"
                                name="insertion"
                                type="text"
                                :label="$t('form.insertionLabel')" />
                            <Text
                                v-model="body.lastName.value"
                                :error-message="body.lastName?.errorMessage"
                                :validation-type="body.lastName?.errorMessage ? 'error' : undefined"
                                name="lastName"
                                type="text"
                                :required="true"
                                :label="$t('form.lastNameLabel')" />
                            <Text
                                v-model="body.email.value"
                                :error-message="body.email?.errorMessage"
                                :validation-type="body.email?.errorMessage ? 'error' : undefined"
                                type="email"
                                name="email"
                                :required="true"
                                :label="$t('form.emailAddressLabel')" />
                        </div>
                    </div>
                    <hr class="text-black/10" />
                    <div class="grid gap-4">
                        <div class="flex flex-col gap-2">
                            <h6 class="font-body w-full font-semibold">
                                {{ $t('order.accountCreationHeader') }}
                            </h6>
                            <Text
                                v-if="flow.passwordDisclaimer"
                                v-model="body.password.value"
                                :error-message="body.password?.errorMessage"
                                :validation-type="body.password?.errorMessage ? 'error' : undefined"
                                name="password"
                                type="password"
                                :required="true"
                                :label="$t('order.passwordLabel')"
                                validated="info"
                                :info-message="flow.passwordDisclaimer" />
                        </div>
                        <Text
                            v-model="body.repeatPassword.value"
                            :error-message="body.repeatPassword?.errorMessage"
                            :validation-type="
                                body.repeatPassword?.errorMessage ? 'error' : undefined
                            "
                            type="password"
                            name="repeat-password"
                            :required="true"
                            :label="$t('order.repeatPasswordLabel')" />
                    </div>
                    <div class="grid gap-2">
                        <button type="submit" class="btn-primary-a">
                            {{ flow.buttonText }}
                        </button>
                        <NuxtLink
                            v-for="(additionalLink, index) in flow.additionalLinks"
                            :key="additionalLink.url ?? index"
                            :to="localeRoute(additionalLink.url)"
                            :target="additionalLink.openInNewTab ? '_blank' : '_self'"
                            class="btn-tertiary-a inline-button">
                            {{ additionalLink.title }}
                        </NuxtLink>
                    </div>
                </div>
            </form>
        </div>

        <ClientOnly>
            <Teleport to="body">
                <AlertToast :alert-settings="alertSettings" @hide-alert="hideAlert" />
            </Teleport>
        </ClientOnly>
    </section>
</template>
